/**
 * Alarms slice — the configurable underway alarms reached from the Live menu.
 *
 * Config is what the user sets (each alarm on/off plus its threshold); live is
 * what's currently firing, computed in MapView where the chart cells and the
 * boat's position both live, then read by the alarm banner so it stays the one
 * place warnings are raised. AIS alarms aren't here yet because we have no AIS
 * source to feed them; the sheet shows them locked rather than pretending.
 */
import { create } from 'zustand';
import { loadJson, saveJson } from './persist';

export interface AlarmsConfig {
  /** Charted-hazard proximity: warn when a charted danger is within this range. */
  proximityEnabled: boolean;
  /** Range, nm. */
  proximityNm: number;
  /** Charted depth under the boat: warn when it drops below this. */
  depthEnabled: boolean;
  /** Depth limit, metres. Now read as least depth UNDER THE KEEL (tide-corrected). */
  depthLimitM: number;
  // Boat draught is NOT here — it lives in the boat-profile settings store
  // (useSettingsStore) as the single source of truth, shared with the depth-under-
  // keel calc and the Settings sheet, so there aren't two draughts to keep in sync.
  /** Master switch for clearing-bearing cross guards (each clearing also has its own flag). */
  clearingEnabled: boolean;
  /** Master switch for clearing-depth (do-not-cross contour) guards. */
  clearingDepthEnabled: boolean;
  /** Grounding alarm: project the track ahead against charted depth / safety depth. */
  groundingEnabled: boolean;
  /** Tide-height alarm: warn when the height of tide crosses a limit. */
  tideHeightEnabled: boolean;
  tideHeightMode: 'below' | 'above';
  tideHeightLimitM: number;
  /** AIS target proximity: warn when a target comes within this range. */
  aisProximityEnabled: boolean;
  aisProximityNm: number;
  /** AIS CPA: warn when a target's CPA is under aisCpaNm AND closing within aisTcpaMin. */
  aisCpaEnabled: boolean;
  aisCpaNm: number;
  aisTcpaMin: number;
  /** Poor GNSS accuracy: warn when horizontal accuracy is worse than the limit. */
  gnssAccuracyEnabled: boolean;
  gnssAccuracyM: number;
  /** Lost GNSS fix: warn when no valid fix for a while (rides the fix-health band). */
  gnssLostEnabled: boolean;
  /** Sudden GNSS position jump (possible spoofing): warn on an implausible jump. */
  gnssJumpEnabled: boolean;
  /** Low battery: warn when the device battery drops below the limit (not charging). */
  lowBatteryEnabled: boolean;
  lowBatteryPct: number;
}

export const DEFAULT_ALARMS_CONFIG: AlarmsConfig = {
  proximityEnabled: false,
  proximityNm: 0.25,
  depthEnabled: false,
  depthLimitM: 1,
  clearingEnabled: true,
  clearingDepthEnabled: true,
  groundingEnabled: false,
  tideHeightEnabled: false,
  tideHeightMode: 'below',
  tideHeightLimitM: 1,
  aisProximityEnabled: false,
  aisProximityNm: 1,
  aisCpaEnabled: false,
  aisCpaNm: 0.5,
  aisTcpaMin: 20,
  gnssAccuracyEnabled: false,
  gnssAccuracyM: 25,
  gnssLostEnabled: false,
  gnssJumpEnabled: false,
  lowBatteryEnabled: false,
  lowBatteryPct: 15,
};

/**
 * What's currently firing, recomputed from the live position and the chart
 * cells. Null on each one means it's off or nothing's triggering it.
 */
export interface AlarmsLive {
  proximity: { label: string; rangeNm: number } | null;
  /**
   * Shallow-water alarm: the actual depth under the keel and how it's made up
   * (charted depth + height of tide now − draught), with the limit it broke.
   */
  depth: {
    underKeelM: number;
    chartedM: number;
    tideM: number | null;
    draughtM: number;
    limitM: number;
  } | null;
  /** Clearing bearings the vessel has crossed to the unsafe side of. */
  clearing: { id: string; label: string }[] | null;
  /** Clearing depths the boat has crossed onto the shallow side of. */
  clearingDepth: { id: string; label: string; valueM: number; chartedM: number }[] | null;
  /**
   * Anchor watch: the boat has dragged outside the boundary. radiusNm is the
   * swing-circle limit in radius mode, or 0 in custom-zone mode (no single
   * radius); mode says which boundary tripped.
   */
  anchor: { distanceNm: number; radiusNm: number; mode?: 'radius' | 'zone' } | null;
  /** Grounding: the projected track reaches unsafe water ahead. */
  grounding: { etaMin: number; distanceNm: number; clearanceM: number | null } | null;
  /** Tide-height alarm: the height now has crossed the limit. */
  tideHeight: { heightM: number; limitM: number; mode: 'below' | 'above' } | null;
  /** AIS warnings: a nearby target and/or one closing to a small CPA. */
  ais: {
    proximity: { label: string; rangeNm: number } | null;
    cpa: { label: string; cpaNm: number; tcpaMin: number } | null;
  };
  /** Route/goto: the boat is inside a waypoint or mark's arrival radius. */
  waypointArrival: { label: string } | null;
  /** Route: the boat is beyond the followed leg's cross-track limit (XTD). */
  offTrack: { xteNm: number; xtdNm: number } | null;
  /** GNSS horizontal accuracy is worse than the set limit (device feed only). */
  gnssAccuracy: { accuracyM: number; limitM: number } | null;
  /** No valid GNSS fix for a while (device feed only). */
  gnssLost: { ageSec: number | null } | null;
  /** An implausible GNSS position jump was seen recently (device feed only). */
  gnssJump: { sinceMs: number } | null;
  /** Device battery is below the limit and not charging. */
  lowBattery: { levelPct: number } | null;
}

interface AlarmsState {
  config: AlarmsConfig;
  setConfig: (patch: Partial<AlarmsConfig>) => void;
  live: AlarmsLive;
  setLive: (live: AlarmsLive) => void;
  /**
   * Master mute: silence every alarm's sound, haptic buzz and screen-off
   * notification in one place, while the alarms themselves stay armed and keep
   * detecting. The warning banner still shows, so a muted alarm is quiet, not
   * blind. Deliberately NOT persisted: it resets to false on a fresh app start,
   * so a restart always un-silences and a skipper can't strand themselves with
   * every alarm quietly muted after the app is reaped in the background.
   */
  masterMuted: boolean;
  setMasterMuted: (muted: boolean) => void;
  toggleMasterMuted: () => void;
  /**
   * Acknowledged alarms (fb-msn7sgm6-0: "tapping the alarm should let me clear
   * it"). Keys are alarm identities (an AlarmKey, or 'at:<id>' / 'offtrack:<id>'
   * for the inline ones). Acknowledging silences THIS episode, banner and siren
   * both; detection is untouched, and when the condition clears the ack is
   * pruned so a fresh episode alarms again. Deliberately not persisted, the
   * same reasoning as master mute.
   */
  acked: Record<string, true>;
  acknowledgeAlarms: (keys: string[]) => void;
  pruneAcked: (activeKeys: string[]) => void;
}

// Armed alarms + their thresholds must survive an app kill/restart, or a skipper
// who set a grounding/depth/CPA guard would silently lose it when Android reaps
// the app in the background. Merged over defaults so a newer alarm field added
// after the profile was saved still gets its default rather than undefined.
const ALARMS_LS = 'acp.alarms.config.v1';

export const useAlarmsStore = create<AlarmsState>((set) => ({
  config: loadJson<AlarmsConfig>(ALARMS_LS, DEFAULT_ALARMS_CONFIG),
  setConfig: (patch) =>
    set((s) => {
      const config = { ...s.config, ...patch };
      saveJson(ALARMS_LS, config);
      return { config };
    }),
  live: { proximity: null, depth: null, clearing: null, clearingDepth: null, anchor: null, grounding: null, tideHeight: null, ais: { proximity: null, cpa: null }, waypointArrival: null, offTrack: null, gnssAccuracy: null, gnssLost: null, gnssJump: null, lowBattery: null },
  setLive: (live) => set({ live }),
  masterMuted: false,
  setMasterMuted: (masterMuted) => set({ masterMuted }),
  toggleMasterMuted: () => set((s) => ({ masterMuted: !s.masterMuted })),
  acked: {},
  acknowledgeAlarms: (keys) =>
    set((s) => {
      const acked = { ...s.acked };
      for (const k of keys) acked[k] = true;
      return { acked };
    }),
  pruneAcked: (activeKeys) =>
    set((s) => {
      const live = new Set(activeKeys);
      const next: Record<string, true> = {};
      let changed = false;
      for (const k of Object.keys(s.acked)) {
        if (live.has(k)) next[k] = true;
        else changed = true;
      }
      return changed ? { acked: next } : s;
    }),
}));
